import { LitElement, classMap, css, html, ifDefined } from '@lit'

class NovaCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    position: {
      type: String,
    },
    radio: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    error: {
      type: Boolean,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
  }

  constructor() {
    super()
    this.uuid = Math.random() * (1000000 - 1) + 1
    this.position = 'bottom'
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this._handleBodyClick)

    if (this.active) {
      this._updateActiveState(false)
      this.setAttribute('selected', '')
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.addEventListener('click', this._handleBodyClick)
  }

  _updateActiveState(isActive) {
    if (isActive) {
      this.setAttribute('active', '')
    } else {
      this.removeAttribute('active')
    }
  }

  _handleBodyClick = (event) => {
    const path = event.composedPath()
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  toggle(event = false) {
    if (
      event &&
      event.type === 'keyup' &&
      !~['ESCAPE'].indexOf(event.code.toUpperCase())
    ) {
      return true
    }

    this._active = !this._active
    this._updateActiveState(this._active)

    if (this._selected) {
      this.setAttribute('selected', '')
    } else {
      this.removeAttribute('selected')
    }
  }

  selectValue(event, value, isRadio = false) {
    if (!isRadio) {
      event.preventDefault()
    }
    this._selected = value
    const $input = this.querySelector('[slot="input"]') || {}
    $input.value = value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          value,
        },
      }),
    )
    this.toggle()
  }

  get selectedLabel() {
    const option = this.options.find(
      (option) => option.value === this._selected,
    )
    if (!option) return ''
    return option.label
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  render() {
    return html`<div ?disabled=${this.disabled}>
      <div class="container">
        <div id=${`${this.uuid}-label`}>
          <slot name="title"></slot>
        </div>
        <button
          @click=${this.toggle}
          type="button"
          class="button"
          aria-expanded=${this._active}
          aria-controls=${`${this.uuid}-list`}
          aria-describedby=${`${this.uuid}-label`}
          ?disabled=${this.disabled}
        >
          <span class="button__label">${this.selectedLabel}</span>
          <slot name="icon"></slot>
        </button>
        <slot name="input"></slot>
        <nova-focus-trap active=${this._active}>
          <ul
            id=${`${this.uuid}-list`}
            class=${classMap({
              list: true,
              'is-active': this._active,
              'position-top': this.position === 'top',
            })}
            @keyup=${this.toggle}
            aria-hidden=${!this._active}
            role="list"
          >
          ${this.options.map((option) => {
            if (this.lang) {
              option.lang = option.value
            }
            return html`
              <li
                class=${classMap({
                  option: true,
                  'is-active': option.value === this._selected,
                })}
              >
                ${this.radio ? html`
                  <input type="radio" id=${`${this.uuid}-radio-${option.value}`} name=${`${this.uuid}-radio`} value=${option.value} />
                  <div class="radio-checkbox">
                    <div class="radio-checkbox__inner"></div>
                  </div>
                  <label for=${`${this.uuid}-radio-${option.value}`} class="link" @click=${(event) => this.selectValue(event, option.value, true)}>
                    ${this.sanitize(option.label)}
                  </label>
                ` : html`
                  <a
                    @click=${(event) => this.selectValue(event, option.value)}
                    class="link"
                    href="#"
                    hreflang=${ifDefined(option?.lang)}
                    lang=${ifDefined(option?.lang)}
                  >
                    ${this.sanitize(option.label)}
                  </a>
                `}
              </li>
            `
          })}
          </ul>
        </nova-focus-trap>
      </div>
      <slot name="error"></slot>
    </div>`
  }

  static styles = css`
    :host {
      width: 100%;
    }

    .container {
      position: relative;
    }

    .button {
      align-items: center;
      background: transparent;
      border: var(--nova-border-width, 1px) solid currentColor;
      cursor: pointer;
      display: flex;
      font: var(--nova-body-font);
      height: var(--nova-height, 35px);
      justify-content: space-between;
      padding: 0 10px;
      width: 100%;
      border-radius: 2px;
    }

    .button:disabled {
      cursor: not-allowed;
    }

    .button__label {
      position: relative;
      top: 8px;
    }

    .button:focus {
      border-color: transparent;
      border-radius: 2px;
      box-shadow: 0 0 0 2px var(--nova-color-focus);
    }

    :host([error]) .button {
      border-color: transparent;
      border-radius: 2px;
      box-shadow: 0 0 0 2px var(--nova-color-danger);
    }

    ::slotted([slot='button']),
    ::slotted([slot='icon']) {
      align-items: center;
      display: flex;
      height: 100%;
    }

    ::slotted([slot='icon']) {
      width: 10px;
      transition: transform var(--nova-duration-default, 300ms);
    }

    :host([active]) ::slotted([slot='icon']) {
      transform: rotate(180deg);
    }

    ::slotted([slot='title']) {
      color: var(--nova-color-subdued);
      left: 10px;
      margin: 0;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      transition: transform var(--nova-duration-default, 300ms);
    }

    :host([active]) ::slotted([slot='title']),
    :host([selected]) ::slotted([slot='title']) {
      transform: translateY(-20px);
    }

    :host([disabled]) ::slotted([slot='title']) {
      color: var(--nova-color-grey-300);
    }

    .list {
      background-color: var(--nova-background, white);
      border: var(--nova-border-width, 1px) solid var(--nova-color-grey-300);
      display: block;
      margin: 0;
      max-height: var(--nova-options-max-height, 19rem);
      max-width: var(--nova-options-max-width, 22rem);
      min-height: var(--nova-options-min-height, 8.2rem);
      min-width: var(--nova-options-min-width, 12rem);
      opacity: 0;
      padding-left: 0;
      position: absolute;
      transform: translateY(0);
      transition: visibility var(--nova-duration-default, 300ms)
          var(--nova-duration-default, 300ms),
        opacity var(--nova-duration-default, 300ms),
        transform var(--nova-duration-default, 300ms);
      visibility: hidden;
      width: 100%;
      z-index: 1;
      border-radius: 2px;
      box-sizing: border-box;
      box-shadow: 4px 4px 30px 0px rgba(0, 0, 0, 0.17);
    }

    .list.position-top {
      bottom: 100%;
    }

    .list.is-active {
      opacity: 1;
      transform: translateY(-20px);
      transition: transform var(--nova-duration-default, 300ms) 1ms,
        opacity var(--nova-duration-default, 300ms) 1ms, visibility 1ms;
      visibility: visible;
    }

    .option {
      list-style: none;
      display: flex;
      align-items: center;
    }

    .option:not(:last-child) {
      border-bottom: 1px solid var(--nova-color-grey-600);
    }

    .link {
      color: inherit;
      display: block; 
      padding: 13px 10px;
      text-decoration: none;
      font: var(--nova-body-font);
      cursor: pointer;
      flex: 1;
    }

    .link:focus {
      border-radius: 2px;
      box-shadow: 0 0 0 2px var(--nova-color-focus);
    }

    .option input[type="radio"] {
      width: 0;
      height: 0;
    }

    .radio-checkbox {
      width: 10px;
      height: 10px;
      border: 1px solid var(--nova-color-grey-600);
      border-radius: 50%;
      padding: 3px;
    }

    .radio-checkbox__inner {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: var(--nova-color-grey-600);
      display: none;
    }

    .option input[type="radio"]:checked + .radio-checkbox .radio-checkbox__inner {
      display: block;
    }

    ::slotted([slot='error']) {
      margin-top: 10px !important;
      display: flex;
      gap: 10px;
      align-items: center;
      color: var(--nova-color-danger);
      font: var(--nova-body-caption);
    }
  `
}

customElements.define('nova-custom-select', NovaCustomSelect)
